import { toast as sonnerToast, type ToasterProps } from 'sonner';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface ToastOptions {
    duration?: number;
    action?: {
        label: string;
        onClick: () => void;
    };
}

const DEFAULT_TOAST_OPTIONS: ToasterProps = {
    duration: 15000,
    position: 'top-right',
};

/**
 * Toast notification utility using Sonner
 */
export const toast = {
    /**
     * Show a success toast
     */
    success: (message: string, options?: ToastOptions) => {
        sonnerToast.success(message, {
            ...DEFAULT_TOAST_OPTIONS,
            duration: options?.duration || DEFAULT_TOAST_OPTIONS.duration,
            action: options?.action,
            style: {
                backgroundColor: 'hsl(var(--alert-success-bg))',
                color: 'hsl(var(--alert-success))',
                border: '1px solid hsl(var(--alert-success-border))',
            },
        });
    },

    /**
     * Show an error toast
     */
    error: (message: string, options?: ToastOptions) => {
        sonnerToast.error(message, {
            ...DEFAULT_TOAST_OPTIONS,
            duration: options?.duration || DEFAULT_TOAST_OPTIONS.duration,
            action: options?.action,
            style: {
                backgroundColor: 'hsl(var(--alert-error-bg))',
                color: 'hsl(var(--alert-error))',
                border: '1px solid hsl(var(--alert-error-border))',
            },
        });
    },

    /**
     * Show a warning toast
     */
    warning: (message: string, options?: ToastOptions) => {
        sonnerToast.warning(message, {
            ...DEFAULT_TOAST_OPTIONS,
            duration: options?.duration || DEFAULT_TOAST_OPTIONS.duration,
            action: options?.action,
            style: {
                backgroundColor: 'hsl(var(--alert-warning-bg))',
                color: 'hsl(var(--alert-warning))',
                border: '1px solid hsl(var(--alert-warning-border))',
            },
        });
    },

    /**
     * Show an info toast
     */
    info: (message: string, options?: ToastOptions) => {
        sonnerToast.info(message, {
            ...DEFAULT_TOAST_OPTIONS,
            duration: options?.duration || DEFAULT_TOAST_OPTIONS.duration,
            action: options?.action,
            style: {
                backgroundColor: 'hsl(var(--alert-info-bg))',
                color: 'hsl(var(--alert-info))',
                border: '1px solid hsl(var(--alert-info-border))',
            },
        });
    },

    /**
     * Show a loading toast
     */
    loading: (message: string) => {
        return sonnerToast.loading(message, {
            ...DEFAULT_TOAST_OPTIONS,
            style: {
                backgroundColor: 'hsl(var(--alert-info-bg))',
                color: 'hsl(var(--alert-info))',
                border: '1px solid hsl(var(--alert-info-border))',
            },
        });
    },

    /**
     * Dismiss a toast
     */
    dismiss: (toastId: string | number) => {
        sonnerToast.dismiss(toastId);
    },
};
